import React, { useEffect, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const STATUS_BADGE = {
  draft:'badge-gray', submitted:'badge-amber', mgr_approved:'badge-blue',
  hr_approved:'badge-teal', client_approved:'badge-green',
  rejected:'badge-red', needs_revision:'badge-amber'
};
const DT_COLOR = {
  PRESENT:'#10b981', HALF_DAY:'#3b82f6', LEAVE:'#f59e0b',
  CLIENT_HOLIDAY:'#8b5cf6', WEEKLY_OFF:'#6b7280', COMP_OFF:'#ec4899'
};
const DT_LABEL = {
  PRESENT:'Present', HALF_DAY:'Half Day', LEAVE:'Leave',
  CLIENT_HOLIDAY:'Client Holiday', WEEKLY_OFF:'Weekly Off', COMP_OFF:'Comp Off'
};

const formatMonth = (dateStr) => {
  try { return format(new Date(dateStr), 'MMMM yyyy'); } catch { return dateStr; }
};

export default function MyTimesheets() {
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [expanded, setExpanded]     = useState(null);
  const [submitting, setSubmitting] = useState(null); // holds ts.id being submitted

  const load = () => {
    setLoading(true);
    api.get('/timesheets/me')
      .then(r => setTimesheets(r.data.timesheets))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const totalBillable = (ts) =>
    (ts.entries || []).reduce((s, e) =>
      s + (e.day_type === 'PRESENT' ? parseFloat(e.hours || 0) : e.day_type === 'HALF_DAY' ? 4 : 0), 0
    ).toFixed(1);

  const countType = (ts, type) => (ts.entries || []).filter(e => e.day_type === type).length;

  const handleResubmit = async (ts) => {
    if (!window.confirm('Resubmit this timesheet for manager approval?')) return;
    setSubmitting(ts.id);
    try {
      await api.post(`/timesheets/me/${ts.id}/submit`);
      toast.success('Resubmitted for manager approval!');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resubmit.');
    } finally { setSubmitting(null); }
  };

  if (loading) return <div className="loading">Loading…</div>;

  return (
    <div>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:700, letterSpacing:'-.4px' }}>My Submissions</h1>
          <p style={{ fontSize:13, color:'#6b7280', marginTop:3 }}>
            {timesheets.length} timesheet{timesheets.length !== 1 ? 's' : ''} submitted
          </p>
        </div>
      </div>

      {timesheets.length === 0 ? (
        <div className="card">
          <div className="empty">
            <div className="empty-icon">📋</div>
            <p>No timesheets yet. Start logging on the Calendar tab.</p>
          </div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {timesheets.map(ts => (
            <div key={ts.id} className="card" style={{ padding:0, overflow:'hidden' }}>

              {/* ── Header row ── */}
              <div
                style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 18px', cursor:'pointer' }}
                onClick={() => setExpanded(expanded === ts.id ? null : ts.id)}
              >
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <span style={{ fontWeight:600, fontSize:14 }}>
                      Month of {formatMonth(ts.week_start)}
                    </span>
                    <span className={`badge ${STATUS_BADGE[ts.status]}`}>
                      {ts.status.replace(/_/g, ' ')}
                    </span>
                  </div>

                  {/* Rejection / revision warning */}
                  {(ts.status === 'rejected' || ts.status === 'needs_revision') && (
                    <div style={{ marginTop:5, fontSize:12.5, color: ts.status==='rejected' ? '#ef4444' : '#f59e0b' }}>
                      {ts.status === 'rejected'
                        ? `⚠ Rejected${ts.mgr_comments ? `: "${ts.mgr_comments}"` : ' by manager'}`
                        : `↩ Revision requested${ts.hr_comments ? `: "${ts.hr_comments}"` : ' by HR'}`
                      }
                    </div>
                  )}

                  {/* Attendance summary */}
                  <div style={{ display:'flex', gap:8, marginTop:6, flexWrap:'wrap' }}>
                    {countType(ts,'PRESENT')>0        && <span style={{fontSize:11,color:'#10b981'}}>✓ {countType(ts,'PRESENT')} Present</span>}
                    {countType(ts,'HALF_DAY')>0       && <span style={{fontSize:11,color:'#3b82f6'}}>½ {countType(ts,'HALF_DAY')} Half</span>}
                    {countType(ts,'LEAVE')>0          && <span style={{fontSize:11,color:'#f59e0b'}}>🏖 {countType(ts,'LEAVE')} Leave</span>}
                    {countType(ts,'CLIENT_HOLIDAY')>0 && <span style={{fontSize:11,color:'#8b5cf6'}}>🎉 {countType(ts,'CLIENT_HOLIDAY')} Holiday</span>}
                    {countType(ts,'WEEKLY_OFF')>0     && <span style={{fontSize:11,color:'#6b7280'}}>🔴 {countType(ts,'WEEKLY_OFF')} Off</span>}
                    {countType(ts,'COMP_OFF')>0       && <span style={{fontSize:11,color:'#ec4899'}}>💆 {countType(ts,'COMP_OFF')} Comp Off</span>}
                  </div>
                </div>

                {/* Right side — billable hrs + resubmit */}
                <div style={{ display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:20, fontWeight:700, color:'#6c63ff' }}>{totalBillable(ts)}</div>
                    <div style={{ fontSize:11, color:'#6b7280' }}>billable hrs</div>
                  </div>

                  {/* Resubmit button for rejected or needs_revision */}
                  {(ts.status === 'rejected' || ts.status === 'needs_revision') && (
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={submitting === ts.id}
                      onClick={(e) => { e.stopPropagation(); handleResubmit(ts); }}
                    >
                      {submitting === ts.id ? '…' : '📤 Resubmit'}
                    </button>
                  )}

                  <span style={{ color:'#6b7280', fontSize:14 }}>{expanded === ts.id ? '▲' : '▼'}</span>
                </div>
              </div>

              {/* ── Expanded detail ── */}
              {expanded === ts.id && (
                <div style={{ borderTop:'1px solid #2a2d3a' }}>

                  {/* Approval trail */}
                  <div style={{ padding:'10px 18px', background:'#0f1117', display:'flex', gap:6, flexWrap:'wrap' }}>
                    {ts.submitted_at && (
                      <span style={{ fontSize:11.5, color:'#9ca3af', background:'#1a1d27', border:'1px solid #2a2d3a', borderRadius:20, padding:'3px 10px' }}>
                        📤 Submitted {format(new Date(ts.submitted_at), 'MMM d')}
                      </span>
                    )}
                    {ts.mgr_reviewed_at && (
                      <span style={{ fontSize:11.5, color:'#9ca3af', background:'#1a1d27', border:'1px solid #2a2d3a', borderRadius:20, padding:'3px 10px' }}>
                        👤 Manager {format(new Date(ts.mgr_reviewed_at), 'MMM d')}
                      </span>
                    )}
                    {ts.hr_reviewed_at && (
                      <span style={{ fontSize:11.5, color:'#9ca3af', background:'#1a1d27', border:'1px solid #2a2d3a', borderRadius:20, padding:'3px 10px' }}>
                        🏢 HR {format(new Date(ts.hr_reviewed_at), 'MMM d')}
                      </span>
                    )}
                    {ts.sent_to_client_at && (
                      <span style={{ fontSize:11.5, color:'#4ecdc4', background:'rgba(78,205,196,.08)', border:'1px solid rgba(78,205,196,.2)', borderRadius:20, padding:'3px 10px' }}>
                        📨 Sent to Client {format(new Date(ts.sent_to_client_at), 'MMM d')}
                      </span>
                    )}
                    {ts.client_approved_at && (
                      <span style={{ fontSize:11.5, borderRadius:20, padding:'3px 10px', color: ts.status==='client_approved'?'#10b981':'#ef4444', background: ts.status==='client_approved'?'rgba(16,185,129,.08)':'rgba(239,68,68,.08)', border:`1px solid ${ts.status==='client_approved'?'rgba(16,185,129,.2)':'rgba(239,68,68,.2)'}` }}>
                        {ts.status === 'client_approved' ? '✅' : '❌'} {ts.client_approved_by} · {format(new Date(ts.client_approved_at), 'MMM d')}
                      </span>
                    )}
                  </div>

                  {/* Entries table */}
                  <div className="table-wrap" style={{ borderRadius:0, border:'none', borderTop:'1px solid #2a2d3a' }}>
                    <table>
                      <thead>
                        <tr><th>Date</th><th>Type</th><th>Project</th><th>Hours</th><th>Description</th></tr>
                      </thead>
                      <tbody>
                        {(ts.entries || []).length === 0 ? (
                          <tr><td colSpan={5} style={{ textAlign:'center', color:'#6b7280' }}>No entries</td></tr>
                        ) : (ts.entries || []).map(e => (
                          <tr key={e.id}>
                            <td style={{ whiteSpace:'nowrap' }}>{format(new Date(e.date), 'EEE, MMM d')}</td>
                            <td>
                              <span style={{ display:'inline-flex', alignItems:'center', padding:'2px 8px', borderRadius:20, fontSize:11.5, fontWeight:600, background:`${DT_COLOR[e.day_type]}22`, color:DT_COLOR[e.day_type] }}>
                                {DT_LABEL[e.day_type]}
                              </span>
                            </td>
                            <td style={{ fontSize:12.5 }}>{e.project?.name || '—'}</td>
                            <td><strong>{e.day_type === 'HALF_DAY' ? '4.0' : e.hours || '—'}</strong></td>
                            <td style={{ fontSize:12, color:'#9ca3af', maxWidth:200 }}>{e.description || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Comments from approvers */}
                  {(ts.mgr_comments || ts.hr_comments || ts.client_comments) && (
                    <div style={{ padding:'12px 18px', borderTop:'1px solid #2a2d3a', display:'flex', flexDirection:'column', gap:6 }}>
                      {ts.mgr_comments    && <div style={{ fontSize:12.5 }}><span style={{ color:'#6b7280' }}>Manager: </span>{ts.mgr_comments}</div>}
                      {ts.hr_comments     && <div style={{ fontSize:12.5 }}><span style={{ color:'#6b7280' }}>HR: </span>{ts.hr_comments}</div>}
                      {ts.client_comments && <div style={{ fontSize:12.5 }}><span style={{ color:'#6b7280' }}>Client: </span>{ts.client_comments}</div>}
                    </div>
                  )}

                  {/* Resubmit section at bottom of expanded */}
                  {(ts.status === 'rejected' || ts.status === 'needs_revision') && (
                    <div style={{ padding:'12px 18px', borderTop:'1px solid #2a2d3a', background:'rgba(108,99,255,.04)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <span style={{ fontSize:13, color: ts.status==='rejected'?'#ef4444':'#f59e0b' }}>
                        {ts.status === 'rejected'
                          ? '⚠ Update your entries on the Calendar tab, then resubmit.'
                          : '↩ Make corrections on the Calendar tab, then resubmit.'
                        }
                      </span>
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={submitting === ts.id}
                        onClick={() => handleResubmit(ts)}
                      >
                        {submitting === ts.id ? 'Submitting…' : '📤 Resubmit'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}